import { useMemo } from 'react';

import { type Library, WorkspaceMemberRoleDisplayName, WorkspaceMemberType } from '@air/api/types';
import { convertUnknownToError } from '@air/utils-error';

import { useLibraryUpdateMutation } from '~/components/LibraryBeta/hooks/mutations/useLibraryUpdateMutation';
import { useLibrary } from '~/components/LibraryBeta/hooks/queries/useLibrary';
import {
  LibraryAdvancedSettingsDefaultRole,
  type LibraryAdvancedSettingsDefaultRoleProps,
} from '~/components/LibraryBeta/LibrarySettingsModal/components/LibraryAdvancedSettings/components/LibraryAdvancedSettingsDefaultRole';
import { useLibraryToasts } from '~/hooks/useLibraryToasts';
import { useWorkspaceRoles } from '~/swr-hooks/workspaces/useWorkspaceRoles';

export type LibraryAdvancedSettingsDefaultRoleContainerProps = {
  libraryId: Library['id'];
  onChangeRole?: () => void;
};

export const LibraryAdvancedSettingsDefaultRoleContainer = ({
  libraryId,
  onChangeRole,
}: LibraryAdvancedSettingsDefaultRoleContainerProps) => {
  const { data: library } = useLibrary({ libraryId });
  const { libraryUpdateMutation } = useLibraryUpdateMutation({ libraryId });
  const { data: roles } = useWorkspaceRoles();
  const { showLibraryToast } = useLibraryToasts();

  const selectedRole = useMemo<LibraryAdvancedSettingsDefaultRoleProps['selectedRole']>(() => {
    if (!library?.defaultRoleId || !roles) {
      return undefined;
    }

    const currentRole = roles.find((role) => role.id === library.defaultRoleId);

    return {
      id: library?.defaultRoleId,
      label: currentRole ? currentRole.displayName.replace('Library ', '') : undefined,
    };
  }, [library, roles]);

  const filteredRoles = useMemo<LibraryAdvancedSettingsDefaultRoleProps['roles']>(() => {
    if (!roles) {
      return [];
    }

    return (
      roles
        .filter(
          (role) =>
            role.type === WorkspaceMemberType.libraryMember &&
            role.displayName !== WorkspaceMemberRoleDisplayName.LibraryOwner,
        )
        // Filter out disabled roles, but always show the library's current default role
        .filter((role) => role.enabled !== false || role.id === library?.defaultRoleId)
        .map((role) => ({
          description: role.description || undefined,
          id: role.id,
          label: role.displayName.replace('Library ', ''),
          onSelect: () => {
            libraryUpdateMutation.mutate(
              { defaultRoleId: role.id },
              {
                onError: (error) => showLibraryToast({ message: convertUnknownToError(error).message, type: 'error' }),
                onSuccess: (data) => {
                  const role = roles.find((role) => role.id === data.defaultRoleId);

                  showLibraryToast({
                    message: role?.displayName ? (
                      <>
                        Default role has been set to <b className="ads:text-accent text-jay-200">{role.displayName}</b>.
                      </>
                    ) : (
                      'Default role has been changed.'
                    ),
                    type: 'info',
                  });
                },
              },
            );

            onChangeRole?.();
          },
          type: 'item',
        }))
    );
  }, [library?.defaultRoleId, libraryUpdateMutation, onChangeRole, roles, showLibraryToast]);

  return (
    <LibraryAdvancedSettingsDefaultRole
      isLoading={libraryUpdateMutation.isPending}
      selectedRole={selectedRole}
      roles={filteredRoles}
    />
  );
};
